// Fixed-timestep driver with accumulator and spiral-of-death guard.
// Conforms to GDD Appendix A §A.2 and §14.5.

import { cfg } from '../config.js';

export interface Stepper {
  step(tick: number): void;
  render(tick: number, alpha: number): void;
}

const STEP = 1 / 60;
const MAX_STEPS = 5;

export class FixedStepDriver {
  private acc = 0;
  private lastTick = 0;
  stepsDropped = 0;
  dilation = 1;
  running = false;
  private rafId = 0;

  constructor(private readonly stepper: Stepper) {}

  frame(rawDtMs: number): void {
    const dt = Math.min(rawDtMs, 250) / 1000;
    this.acc += dt * this.dilation;
    let steps = 0;
    while (this.acc >= STEP && steps < MAX_STEPS) {
      this.stepper.step(this.lastTick);
      this.acc -= STEP;
      this.lastTick++;
      steps++;
    }
    if (this.acc >= STEP) {
      this.stepsDropped += Math.floor(this.acc / STEP);
      this.acc = 0;
    }
    const alpha = this.acc / STEP;
    this.stepper.render(this.lastTick, alpha);
  }

  start(): void {
    if (this.running) return;
    this.running = true;
    let last = performance.now();
    const loop = (now: number) => {
      if (!this.running) return;
      const dt = now - last;
      last = now;
      this.frame(dt);
      this.rafId = requestAnimationFrame(loop);
    };
    this.rafId = requestAnimationFrame(loop);
  }

  stop(): void {
    this.running = false;
    cancelAnimationFrame(this.rafId);
  }

  headlessStep(count: number): void {
    for (let i = 0; i < count; i++) {
      this.stepper.step(this.lastTick);
      this.lastTick++;
    }
  }
}
